import { useEffect, useRef, useState } from "react";

/**
 * Detects when a NEWER build has been deployed while this tab is open.
 *
 * The server reports its build version at GET /api/health. We record it on first load, then re-check
 * periodically (and whenever the tab regains focus). If the version ever differs from the one this
 * page loaded with, a deploy happened → return `true` so the UI can offer a one-click reload.
 *
 * (Hashed asset filenames + `no-store` on index.html already mean a normal reload picks up the new
 * build; this just *tells the user* a reload is worth it instead of leaving them on stale code.)
 */
export function useVersionCheck(intervalMs = 5 * 60 * 1000): boolean {
  const loadedRef = useRef<string | null>(null);
  const [updateAvailable, setUpdateAvailable] = useState(false);

  useEffect(() => {
    let active = true;

    const check = async () => {
      try {
        const res = await fetch("/api/health", { cache: "no-store" });
        if (!res.ok) return;
        const data: { version?: string } = await res.json();
        const v = data.version;
        if (!active || !v) return;
        if (loadedRef.current === null) {
          loadedRef.current = v; // first reading — this is "our" version
        } else if (v !== loadedRef.current) {
          setUpdateAvailable(true); // server moved on → a new build is live
        }
      } catch {
        /* offline / transient network — ignore, try again next tick */
      }
    };

    void check();
    const id = window.setInterval(check, intervalMs);
    const onFocus = () => void check();
    window.addEventListener("focus", onFocus);
    return () => {
      active = false;
      window.clearInterval(id);
      window.removeEventListener("focus", onFocus);
    };
  }, [intervalMs]);

  return updateAvailable;
}
